import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
} from '@angular/common/http';
import { Observable, defer } from 'rxjs';
import { finalize } from 'rxjs/operators';

/** Counts write requests (POST/PUT/PATCH/DELETE) that are still in flight.
 *  Read by BuildVersionService before it swaps an in-app navigation for a
 *  full page load: a page load aborts in-flight requests, so we must not do
 *  it while a save is still on its way to the server. */
@Injectable({ providedIn: 'root' })
export class PendingWritesTracker {
    private count = 0;

    get hasPendingWrites(): boolean {
        return this.count > 0;
    }

    begin(): void {
        this.count++;
    }

    end(): void {
        this.count = Math.max(0, this.count - 1);
    }
}

/** Feeds PendingWritesTracker. Passes every request through unchanged — it
 *  only observes. Reads (GET/HEAD/OPTIONS) are skipped since aborting one
 *  loses nothing. */
@Injectable()
export class PendingWritesInterceptor implements HttpInterceptor {
    private static readonly READ_METHODS = ['GET', 'HEAD', 'OPTIONS'];

    constructor(private tracker: PendingWritesTracker) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (PendingWritesInterceptor.READ_METHODS.includes(request.method)) {
            return next.handle(request);
        }
        // Count from subscription until complete / error / unsubscribe.
        // finalize sits outside defer so it also runs if next.handle throws.
        return defer(() => {
            this.tracker.begin();
            return next.handle(request);
        }).pipe(finalize(() => this.tracker.end()));
    }
}
